import { useFormikContext } from 'formik';
import { useCallback, useRef, useState } from 'react';

import { Button } from '@hivebrite/design-system/Button';
import {
  CATEGORIES,
  FontAwesomeIcon,
} from '@hivebrite/design-system/FontAwesomeIcon';
import { useIsMobileDevice } from '@hivebrite/react-hooks';

import { NotifyPeopleTriggerButton } from 'hb-react/frontoffice/components/NotifyPeopleModal/NotifyPeopleTriggerButton';
import UserTargeting from 'hb-react/frontoffice/components/user_targeting';
import type UserTargetingLegacy from 'hb-react/frontoffice/components/user_targeting/UserTargeting';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';

import type { FormikValues, NotifyPeopleProps, UserProps } from '../types';

const BellIcon = () => (
  <FontAwesomeIcon height="16px" iconName="bell" iconType={CATEGORIES.SOLID} />
);

export const NotifyPeople = ({
  isEditingPost,
  shouldShowNotifyPeople,
  userPost,
}: NotifyPeopleProps) => {
  const { currentUser } = useDefaultAppContexts();
  const { isSubmitting, setFieldValue, values } =
    useFormikContext<FormikValues>();
  const isMobileDevice = useIsMobileDevice();
  const ref = useRef<UserTargetingLegacy>(null);
  const [notifyPeoples, setNotifyPeoples] = useState<UserProps[]>([]);

  const userCanNotify =
    isEditingPost || !shouldShowNotifyPeople
      ? false
      : currentUser?.notify_rights.user_can_notify_on_feed_post;

  // TODO: Remove when removing flagHivehackNotifyPeople
  const handleOpen = useCallback(() => {
    // Select current notifyPeoples in UserTargeting modal if the user re-opens it
    // Only working on desktop by current UserTargeting app design
    if (ref.current && !isMobileDevice) {
      ref.current.selectUsers(notifyPeoples);
    }
  }, [isMobileDevice, notifyPeoples, ref]);

  const handleValidateSelection = useCallback(
    (selectedUsers: UserProps[], selectedUsersIds: number[]) => {
      if (!isMobileDevice) {
        setNotifyPeoples(selectedUsers);
      }
      setFieldValue('targeted_user_ids', selectedUsersIds);
    },
    [isMobileDevice, setFieldValue],
  );

  return userCanNotify ? (
    <UserTargeting
      defaultQuery={{ targeting_type: userPost?.type }}
      defaultSelectedUsers={isMobileDevice ? [] : notifyPeoples}
      maxTargetedUsers={userPost?.user_targeting?.max_targeted_users}
      onOpen={handleOpen}
      onValidateSelection={handleValidateSelection}
      renderTriggerElement={(onClick) => (
        <NotifyPeopleTriggerButton
          values={values}
          isDisabled={isSubmitting}
          onClick={onClick}
        />
      )}
      openByClickOn={
        <Button
          aria-hidden="true"
          as="span"
          dataTestId="user-targeting-button"
          icon={BellIcon}
          isDisabled={isSubmitting}
          variant="secondary"
        >
          {__('Notify people')}
          {values.targeted_user_ids?.length
            ? ` · ${values.targeted_user_ids.length}`
            : null}
        </Button>
      }
      ref={ref}
    />
  ) : null;
};
